import { setMustRefreshAuthentication } from '@redux/authentication';
import { store } from '@redux/store';
import { Auth } from 'aws-amplify';
import request from 'superagent';

const backendBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

class Client {
  baseUrl?: string;
  withCredentials: boolean;
  agent: request.SuperAgentStatic & request.Request;
  tokenKey = 'token';

  constructor(baseUrl?: string, withCredentials = true) {
    this.baseUrl = baseUrl;
    this.withCredentials = withCredentials;
    this.agent = request.agent();
    this.agent.accept('application/json');
    if (withCredentials) {
      this.agent.withCredentials();
    }
  }

  async request(
    method: Method,
    endpoint: string,
    data: object | null = null,
    textResponse = false,
  ) {
    const url = /^https?:\/\//.test(endpoint) ? endpoint : `${this.baseUrl}${endpoint}`;
    const promise = this.getAgentPromise(method, url, data);

    try {
      const response = await promise;

      if (textResponse) {
        return response.text;
      }

      return response.body;
    } catch (error) {
      if (error.status === 401) {
        store.dispatch(setMustRefreshAuthentication(true));
      }
      throw error;
    }
  }

  private async getAgentPromise(method: Method, url: string, data: object | null) {
    let promise = this.agent[method](url);

    const token = await this.getCognitoToken();
    if (token && this.withCredentials) {
      promise = promise.set('Authorization', `Bearer ${token}`);
    }
    if (['post', 'put', 'patch', 'delete'].includes(method) && data) {
      promise = promise.send(data);
    }

    return promise;
  }

  get(endpoint: string, textResponse = false) {
    return this.request('get', endpoint, null, textResponse);
  }

  post(endpoint: string, data: object) {
    return this.request('post', endpoint, data);
  }

  put(endpoint: string, data: object) {
    return this.request('put', endpoint, data);
  }

  patch(endpoint: string, data: object) {
    return this.request('patch', endpoint, data);
  }

  delete(endpoint: string, data = {}) {
    return this.request('delete', endpoint, data);
  }

  async getCognitoToken(): Promise<string | null> {
    try {
      return (await Auth.currentSession()).getAccessToken().getJwtToken();
    } catch {
      // An error is caught if the user is not connected because Auth.currentSession() fails
      // in that case
      return null;
    }
  }
}

const client = new Client(backendBaseUrl);
export const s3Client = new Client(undefined, false);

export default client;
